import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { LoginPage } from '@/modules/auth/pages/LoginPage'
import { RegisterPage } from '@/modules/auth/pages/RegisterPage'
import { RegistrationAllowedRoute } from '@/modules/auth/components/RegistrationAllowedRoute'
import { CheckEmailPage } from '@/modules/auth/pages/CheckEmailPage'
import { VerifyEmailPage } from '@/modules/auth/pages/VerifyEmailPage'
import { ForgotPasswordPage } from '@/modules/auth/pages/ForgotPasswordPage'
import { ResetPasswordPage } from '@/modules/auth/pages/ResetPasswordPage'
import { ProtectedRoute } from '@/components/layout/ProtectedRoute'
import { AdminRoute } from '@/components/layout/AdminRoute'
import { FeatureFlagRoute } from '@/components/layout/FeatureFlagRoute'
import { FeatureFlags } from '@fp3/shared'
import { Spinner } from '@/components/ui/spinner'

const DashboardPage = lazy(() =>
  import('../modules/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
)
const AccountsPage = lazy(() =>
  import('../modules/accounts/pages/AccountsPage').then((m) => ({ default: m.AccountsPage }))
)
const AccountHistoryPage = lazy(() =>
  import('../modules/accounts/pages/AccountHistoryPage').then((m) => ({
    default: m.AccountHistoryPage,
  }))
)
const ExchangeRatesPage = lazy(() =>
  import('../modules/exchange-rates/pages/ExchangeRatesPage').then((m) => ({
    default: m.ExchangeRatesPage,
  }))
)
const CategoriesPage = lazy(() =>
  import('../modules/categories/pages/CategoriesPage').then((m) => ({
    default: m.CategoriesPage,
  }))
)
const ProfilePage = lazy(() =>
  import('../modules/profile').then((m) => ({ default: m.ProfilePage }))
)
const ExpensesPage = lazy(() =>
  import('../modules/expenses').then((m) => ({ default: m.ExpensesPage }))
)
const ImportExpensesPage = lazy(() =>
  import('../modules/expenses').then((m) => ({ default: m.ImportExpensesPage }))
)
const RecurrentExpensesPage = lazy(() =>
  import('../modules/recurrent-expenses').then((m) => ({ default: m.RecurrentExpensesPage }))
)
const IncomesPage = lazy(() =>
  import('../modules/incomes').then((m) => ({ default: m.IncomesPage }))
)
const ImportIncomesPage = lazy(() =>
  import('../modules/incomes').then((m) => ({ default: m.ImportIncomesPage }))
)
const RecurrentIncomesPage = lazy(() =>
  import('../modules/recurrent-incomes').then((m) => ({ default: m.RecurrentIncomesPage }))
)
const TransfersPage = lazy(() =>
  import('../modules/transfers').then((m) => ({ default: m.TransfersPage }))
)
const GroupsPage = lazy(() =>
  import('../modules/groups/pages/GroupsPage').then((m) => ({ default: m.GroupsPage }))
)
const GroupDetailPage = lazy(() =>
  import('../modules/groups/pages/GroupDetailPage').then((m) => ({ default: m.GroupDetailPage }))
)
// per-page imports (not the barrel) so each page produces its own chunk; the barrel
// would force Rollup to merge all investments code into a single shared chunk
const TbankConnectionsPage = lazy(() =>
  import('../modules/investments/pages/TbankConnectionsPage').then((m) => ({
    default: m.TbankConnectionsPage,
  }))
)
const InvestmentsPage = lazy(() =>
  import('../modules/investments/pages/InvestmentsPage').then((m) => ({
    default: m.InvestmentsPage,
  }))
)
const PortfolioPage = lazy(() =>
  import('../modules/investments/pages/PortfolioPage').then((m) => ({ default: m.PortfolioPage }))
)
const OperationsPage = lazy(() =>
  import('../modules/investments/pages/OperationsPage').then((m) => ({
    default: m.OperationsPage,
  }))
)
const OrdersPage = lazy(() =>
  import('../modules/investments/pages/OrdersPage').then((m) => ({ default: m.OrdersPage }))
)
const PaymentsPage = lazy(() =>
  import('../modules/investments/pages/PaymentsPage').then((m) => ({ default: m.PaymentsPage }))
)
const GlobalIncomePage = lazy(() =>
  import('../modules/investments/pages/GlobalIncomePage').then((m) => ({
    default: m.GlobalIncomePage,
  }))
)
const RebalancingPage = lazy(() =>
  import('../modules/investments/pages/RebalancingPage').then((m) => ({
    default: m.RebalancingPage,
  }))
)
const BotPage = lazy(() =>
  import('../modules/investments/pages/BotPage').then((m) => ({ default: m.BotPage }))
)
const BotDebugPage = lazy(() =>
  import('../modules/investments/pages/BotDebugPage').then((m) => ({ default: m.BotDebugPage }))
)
const BotSettingsPage = lazy(() =>
  import('../modules/investments/pages/BotSettingsPage').then((m) => ({
    default: m.BotSettingsPage,
  }))
)
const DayTradeBotPage = lazy(() =>
  import('../modules/investments/pages/DayTradeBotPage').then((m) => ({
    default: m.DayTradeBotPage,
  }))
)
const DayTradeBotDebugPage = lazy(() =>
  import('../modules/investments/pages/DayTradeBotDebugPage').then((m) => ({
    default: m.DayTradeBotDebugPage,
  }))
)
const DayTradePerformancePage = lazy(() =>
  import('../modules/investments/pages/DayTradePerformancePage').then((m) => ({
    default: m.DayTradePerformancePage,
  }))
)
const InstrumentPage = lazy(() =>
  import('../modules/investments/pages/InstrumentPage').then((m) => ({
    default: m.InstrumentPage,
  }))
)
const InvestmentAccountLayout = lazy(() =>
  import('../modules/investments/components/InvestmentAccountLayout').then((m) => ({
    default: m.InvestmentAccountLayout,
  }))
)
const InvestmentAccountRedirect = lazy(() =>
  import('../modules/investments/components/InvestmentAccountRedirect').then((m) => ({
    default: m.InvestmentAccountRedirect,
  }))
)
const OfzScreenerPage = lazy(() =>
  import('../modules/investments/pages/OfzScreenerPage').then((m) => ({
    default: m.OfzScreenerPage,
  }))
)
const BacktestPage = lazy(() =>
  import('../modules/investments/pages/BacktestPage').then((m) => ({
    default: m.BacktestPage,
  }))
)
const SweepResearchPage = lazy(() =>
  import('../modules/investments/pages/SweepResearchPage').then((m) => ({
    default: m.SweepResearchPage,
  }))
)
const DipPipelinePlaygroundPage = lazy(() =>
  import('../modules/investments/pages/DipPipelinePlaygroundPage').then((m) => ({
    default: m.DipPipelinePlaygroundPage,
  }))
)
const FeatureFlagsPage = lazy(() =>
  import('../modules/admin').then((m) => ({ default: m.FeatureFlagsPage }))
)
const AddPluginPage = lazy(() =>
  import('../modules/dashboard/pages/AddPluginPage').then((m) => ({ default: m.AddPluginPage }))
)
const CashFlowReportPage = lazy(() =>
  import('../modules/reports').then((m) => ({ default: m.CashFlowReportPage }))
)
const DepositsReportPage = lazy(() =>
  import('../modules/reports').then((m) => ({ default: m.DepositsReportPage }))
)
const ExpensesByCategoryReportPage = lazy(() =>
  import('../modules/reports').then((m) => ({ default: m.ExpensesByCategoryReportPage }))
)
const ExpenseCompositionReportPage = lazy(() =>
  import('../modules/reports').then((m) => ({ default: m.ExpenseCompositionReportPage }))
)
const IncomesByCategoryReportPage = lazy(() =>
  import('../modules/reports').then((m) => ({ default: m.IncomesByCategoryReportPage }))
)

const PageFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Spinner className="h-8 w-8" />
  </div>
)

export const AppRoutes = () => {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<RegistrationAllowedRoute />}>
          <Route path="/register" element={<RegisterPage />} />
        </Route>
        <Route path="/check-email" element={<CheckEmailPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/dashboard/plugins" element={<AddPluginPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/accounts/exchange-rates" element={<ExchangeRatesPage />} />
          <Route path="/accounts/:id" element={<AccountHistoryPage />} />
          <Route path="/expenses/import" element={<ImportExpensesPage />} />
          <Route path="/expenses/recurrent" element={<RecurrentExpensesPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/incomes/import" element={<ImportIncomesPage />} />
          <Route path="/incomes/recurrent" element={<RecurrentIncomesPage />} />
          <Route path="/incomes" element={<IncomesPage />} />
          <Route path="/transfers" element={<TransfersPage />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/groups/:id" element={<GroupDetailPage />} />
          <Route path="/expenses/categories" element={<CategoriesPage />} />
          <Route path="/incomes/categories" element={<CategoriesPage />} />
          <Route element={<FeatureFlagRoute flag={FeatureFlags.INVESTMENTS} />}>
            <Route path="/investments" element={<InvestmentsPage />} />
            <Route path="/investments/income" element={<GlobalIncomePage />} />
            <Route
              path="/investments/:connectionId/:accountId"
              element={<InvestmentAccountLayout />}
            >
              <Route index element={<InvestmentAccountRedirect />} />
              <Route path="portfolio" element={<PortfolioPage />} />
              <Route path="operations" element={<OperationsPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="rebalancing" element={<RebalancingPage />} />
              <Route path="ofz-screener" element={<OfzScreenerPage />} />
              <Route element={<FeatureFlagRoute flag={FeatureFlags.TRADING_BOT} />}>
                <Route path="bot" element={<BotPage />} />
                <Route path="bot/instruments/:instrumentUid/debug" element={<BotDebugPage />} />
              </Route>
              <Route path="settings" element={<BotSettingsPage />} />
              <Route element={<FeatureFlagRoute flag={FeatureFlags.DAY_TRADE_BOT} />}>
                <Route path="day-trade-bot" element={<DayTradeBotPage />} />
                <Route path="day-trade-bot/performance" element={<DayTradePerformancePage />} />
                <Route
                  path="day-trade-bot/instruments/:instrumentUid/debug"
                  element={<DayTradeBotDebugPage />}
                />
              </Route>
              <Route element={<FeatureFlagRoute flag={FeatureFlags.DAY_TRADE_BACKTEST} />}>
                <Route path="backtest" element={<BacktestPage />} />
                <Route path="backtest/research" element={<SweepResearchPage />} />
              </Route>
              <Route element={<FeatureFlagRoute flag={FeatureFlags.DIP_PIPELINE_PLAYGROUND} />}>
                <Route path="dip-pipeline" element={<DipPipelinePlaygroundPage />} />
              </Route>
              <Route path="instrument/:instrumentUid" element={<InstrumentPage />} />
            </Route>
            <Route path="/settings/investments/tbank" element={<TbankConnectionsPage />} />
          </Route>
          <Route path="/profile" element={<Navigate to="/profile/personal" replace />} />
          <Route path="/profile/:tab" element={<ProfilePage />} />
          <Route path="/reports/cash-flow" element={<CashFlowReportPage />} />
          <Route path="/reports/deposits" element={<DepositsReportPage />} />
          <Route path="/reports/expenses-by-category" element={<ExpensesByCategoryReportPage />} />
          <Route path="/reports/expense-composition" element={<ExpenseCompositionReportPage />} />
          <Route path="/reports/incomes-by-category" element={<IncomesByCategoryReportPage />} />
          <Route element={<AdminRoute />}>
            <Route path="/admin" element={<Navigate to="/admin/feature-flags" replace />} />
            <Route path="/admin/feature-flags" element={<FeatureFlagsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
